import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { creditos, fechaHora, gramos, iso } from "../lib/formato";
import { Aviso, Cabecera, Campo, Cargando, Etiqueta, Modal, Panel, TarjetaDato, Vacio } from "../componentes/piezas";
import { useSesion } from "../lib/sesion";
import { useIdioma } from "../lib/idioma";

/**
 * Recuentos del almacén: se pesa lo que hay y se compara con lo que el programa
 * dice que debería haber.
 *
 * Antes se ajustaba producto a producto, y el cierre de un club son
 * veintitantos botes: quedaban veintitantos apuntes sueltos sin nada que dijera
 * que eran del mismo recuento, ni cuánto descuadraba en total. Ese total es lo
 * que hay que poder enseñar si alguien pregunta por qué falta género.
 */

interface Resumen {
  id: string;
  numero: number;
  estado: "ABIERTO" | "CERRADO";
  tipo: string;
  iniciado: string;
  cerrado: string | null;
  notas: string | null;
  ajustoStock: boolean;
  usuario: { nombre: string } | null;
  productos: number;
  contados: number;
  saltados: number;
  descuadreMg: number;
  minutos: number | null;
}

interface Linea {
  id: string;
  productoId: string;
  esperadoMg: number;
  contadoMg: number | null;
  saltado: boolean;
  notas: string | null;
  producto: {
    id: string;
    nombre: string;
    stockMg: number;
    categoriaId: string;
    categoria: { nombre: string; color: string; unidadMedida: string };
  };
}

interface Detalle extends Omit<Resumen, "productos" | "contados" | "saltados" | "descuadreMg" | "minutos"> {
  lineas: Linea[];
}

/** El descuadre en verde si sobra, en rojo si falta. Cero no se pinta. */
function Descuadre({ mg }: { mg: number }) {
  const { t } = useIdioma();
  if (mg === 0) return <span className="suave">{t("re.cuadra")}</span>;
  return (
    <strong className="tabular" style={{ color: mg < 0 ? "var(--rojo)" : "var(--verde)" }}>
      {mg > 0 ? "+" : ""}
      {gramos(mg)}
    </strong>
  );
}

export default function Recuentos() {
  const { t, cuenta } = useIdioma();
  const { puede } = useSesion();
  const clienteConsultas = useQueryClient();
  const primeroDeMes = new Date();
  primeroDeMes.setDate(1);

  const [desde, setDesde] = useState(iso(primeroDeMes));
  const [hasta, setHasta] = useState(iso(new Date()));
  const [abierto, setAbierto] = useState<string | null>(null);
  const [error, setError] = useState("");

  const lista = useQuery({
    queryKey: ["recuentos", desde, hasta],
    queryFn: () => api<Resumen[]>("/recuentos", { query: { desde, hasta } }),
  });

  const refrescar = () => {
    void clienteConsultas.invalidateQueries({ queryKey: ["recuentos"] });
    void clienteConsultas.invalidateQueries({ queryKey: ["inventario"] });
  };

  const enCurso = (lista.data ?? []).find((r) => r.estado === "ABIERTO") ?? null;
  const cerrados = (lista.data ?? []).filter((r) => r.estado === "CERRADO");

  async function empezar() {
    setError("");
    try {
      const r = await api<{ id: string }>("/recuentos", { metodo: "POST", cuerpo: { tipo: "CIERRE" } });
      refrescar();
      setAbierto(r.id);
    } catch (fallo) {
      setError(fallo instanceof Error ? fallo.message : t("re.falloEmpezar"));
    }
  }

  if (lista.isLoading) return <Cargando />;

  return (
    <>
      <Cabecera titulo={t("re.titulo")} descripcion={t("re.descripcion")}>
        <input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} style={{ width: 160 }} />
        <span className="suave">{t("re.a")}</span>
        <input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} style={{ width: 160 }} />
        {puede("gestionarInventario") && !enCurso && (
          <button className="boton" onClick={empezar}>
            {t("re.empezar")}
          </button>
        )}
      </Cabecera>

      <Aviso tipo="error">{error}</Aviso>

      {enCurso && (
        <Panel titulo={t("re.enMarcha", { numero: enCurso.numero })}>
          <div className="fila separada">
            <div>
              <p style={{ marginTop: 0 }}>
                {t("re.empezado", {
                  cuando: fechaHora(enCurso.iniciado),
                  quien: enCurso.usuario?.nombre ?? t("re.alguien"),
                  contados: enCurso.contados,
                  total: enCurso.productos,
                })}
              </p>
              <p className="suave pequeno" style={{ marginBottom: 0 }}>
                {t("re.conLocalCerrado")}
              </p>
            </div>
            <button className="boton" onClick={() => setAbierto(enCurso.id)}>
              {t("re.seguirContando")}
            </button>
          </div>
        </Panel>
      )}

      <div className="rejilla tres" style={{ margin: "16px 0" }}>
        <TarjetaDato
          etiqueta={t("re.delPeriodo")}
          valor={String(cerrados.length)}
          pie={cerrados.length > 0 ? t("re.cerrados") : t("re.todaviaNinguno")}
        />
        <TarjetaDato
          etiqueta={t("re.descuadreAcumulado")}
          valor={gramos(cerrados.reduce((s, r) => s + r.descuadreMg, 0))}
          pie={t("re.sumandoTodos")}
        />
        <TarjetaDato
          etiqueta={t("re.elPeor")}
          valor={
            cerrados.length > 0
              ? gramos(
                  cerrados.reduce((peor, r) => (Math.abs(r.descuadreMg) > Math.abs(peor) ? r.descuadreMg : peor), 0),
                )
              : "—"
          }
          pie={t("re.elDiaQueMas")}
        />
      </div>

      <Panel sinCuerpo>
        {cerrados.length === 0 ? (
          <Vacio>{t("re.sinCerrados")}</Vacio>
        ) : (
          <div className="tabla-envoltorio">
            <table className="tabla">
              <thead>
                <tr>
                  <th>{t("re.numero")}</th>
                  <th>{t("re.cuando")}</th>
                  <th>{t("re.quien")}</th>
                  <th className="num">{t("re.contados")}</th>
                  <th className="num">{t("re.saltados")}</th>
                  <th className="num">{t("re.descuadre")}</th>
                  <th>{t("re.seCorrigio")}</th>
                  <th className="num">{t("re.tardo")}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {cerrados.map((r) => (
                  <tr key={r.id}>
                    <td className="suave pequeno tabular">#{r.numero}</td>
                    <td className="pequeno">
                      {fechaHora(r.cerrado ?? r.iniciado)}
                      {r.notas && <div className="suave pequeno">{r.notas}</div>}
                    </td>
                    <td className="pequeno suave">{r.usuario?.nombre ?? "—"}</td>
                    <td className="num tabular">
                      {r.contados}/{r.productos}
                    </td>
                    <td className="num tabular">{r.saltados > 0 ? r.saltados : "—"}</td>
                    <td className="num">
                      <Descuadre mg={r.descuadreMg} />
                    </td>
                    <td className="pequeno">
                      {r.ajustoStock ? (
                        <Etiqueta>{t("re.si")}</Etiqueta>
                      ) : (
                        <span className="suave">{t("re.no")}</span>
                      )}
                    </td>
                    <td className="num tabular suave pequeno">
                      {r.minutos !== null ? cuenta(r.minutos, "re.unMinuto", "re.minutos") : "—"}
                    </td>
                    <td>
                      <button className="boton fantasma" onClick={() => setAbierto(r.id)}>
                        {t("re.ver")}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      {abierto && (
        <DialogoRecuento
          id={abierto}
          alCerrar={() => setAbierto(null)}
          alCambiar={refrescar}
          alFallar={setError}
        />
      )}
    </>
  );
}

/** Contar, o leer lo que se contó. */
function DialogoRecuento({
  id,
  alCerrar,
  alCambiar,
  alFallar,
}: {
  id: string;
  alCerrar: () => void;
  alCambiar: () => void;
  alFallar: (m: string) => void;
}) {
  const clienteConsultas = useQueryClient();
  const { t, cuenta } = useIdioma();
  const [cerrando, setCerrando] = useState(false);
  const [ajustar, setAjustar] = useState(true);
  const [notas, setNotas] = useState("");
  const [enviando, setEnviando] = useState(false);

  const detalle = useQuery({ queryKey: ["recuento", id], queryFn: () => api<Detalle>(`/recuentos/${id}`) });
  const refrescarDetalle = () => void clienteConsultas.invalidateQueries({ queryKey: ["recuento", id] });

  const lineas = detalle.data?.lineas ?? [];
  const contadas = useMemo(() => lineas.filter((l) => l.contadoMg !== null && !l.saltado), [lineas]);

  /*
   * Por categorias, que es como estan los botes en la estanteria: se coge la
   * balanza, se pesa toda la flor y se pasa a los extractos. Una lista de
   * cuarenta nombres seguidos obliga a buscar cada uno.
   */
  const grupos = useMemo(() => {
    const mapa = new Map<string, { id: string; nombre: string; lineas: Linea[] }>();
    for (const l of lineas) {
      const grupo = mapa.get(l.producto.categoriaId) ?? {
        id: l.producto.categoriaId,
        nombre: l.producto.categoria.nombre,
        lineas: [],
      };
      grupo.lineas.push(l);
      mapa.set(grupo.id, grupo);
    }
    return [...mapa.values()];
  }, [lineas]);

  const quedan = lineas.filter((l) => l.contadoMg === null && !l.saltado).length;
  const descuadre = contadas.reduce((s, l) => s + (l.contadoMg! - l.esperadoMg), 0);
  const enMarcha = detalle.data?.estado === "ABIERTO";

  /** Guarda lo pesado de una linea, si ha cambiado. */
  function guardarPesada(linea: Linea, valor: string) {
    const escrito = valor.trim();
    const actual = linea.contadoMg !== null ? String(linea.contadoMg / 1000) : "";
    if (escrito === actual) return;
    void apuntar(linea, { contadoG: escrito === "" ? null : Number(escrito) });
  }

  /** Da por saltado lo que quede sin pesar, de todo o de una categoria. */
  async function saltarResto(categoriaId?: string) {
    try {
      await api(`/recuentos/${id}/saltar-resto`, { metodo: "POST", cuerpo: { categoriaId } });
      refrescarDetalle();
      alCambiar();
    } catch (fallo) {
      alFallar(fallo instanceof Error ? fallo.message : t("re.d.falloSaltar"));
    }
  }

  async function apuntar(linea: Linea, cambios: { contadoG?: number | null; saltado?: boolean }) {
    try {
      await api(`/recuentos/${id}/lineas/${linea.productoId}`, { metodo: "PATCH", cuerpo: cambios });
      refrescarDetalle();
      alCambiar();
    } catch (fallo) {
      alFallar(fallo instanceof Error ? fallo.message : t("re.d.falloApuntar"));
    }
  }

  return (
    <Modal
      titulo={detalle.data ? t("re.d.titulo", { numero: detalle.data.numero }) : t("re.d.recuento")}
      alCerrar={alCerrar}
      acciones={
        enMarcha ? (
          <>
            <button className="boton secundario" onClick={alCerrar}>
              {t("re.d.seguirLuego")}
            </button>
            <button className="boton" disabled={contadas.length === 0} onClick={() => setCerrando(true)}>
              {t("re.d.cerrarRecuento")}
            </button>
          </>
        ) : (
          <>
            <button className="boton secundario" onClick={() => window.print()}>
              {t("d.imprimir")}
            </button>
            <button className="boton" onClick={alCerrar}>
              {t("g.cerrar")}
            </button>
          </>
        )
      }
    >
      {detalle.isLoading || !detalle.data ? (
        <Cargando />
      ) : (
        <>
          <div className="fila separada" style={{ marginBottom: 12 }}>
            <span className="suave pequeno">
              {t("re.d.contadosDe", { contados: contadas.length, total: lineas.length })}
              {lineas.filter((l) => l.saltado).length > 0 &&
                t("re.d.nSaltados", { n: lineas.filter((l) => l.saltado).length })}
            </span>
            <span className="fila" style={{ gap: 12 }}>
              {enMarcha && quedan > 0 && (
                <button className="boton fantasma sin-imprimir" onClick={() => void saltarResto()}>
                  {t("re.d.saltarLoQueQueda", { n: quedan })}
                </button>
              )}
              <span>
                {t("re.d.descuadre")} <Descuadre mg={descuadre} />
              </span>
            </span>
          </div>

          <div className="documento">
            <div className="tabla-envoltorio" style={{ maxHeight: 420, overflowY: "auto" }}>
              <table className="tabla">
                <thead>
                  <tr>
                    <th>{t("re.d.producto")}</th>
                    <th className="num">{t("re.d.deberiaHaber")}</th>
                    <th className="num">{t("re.d.hay")}</th>
                    <th className="num">{t("re.descuadre")}</th>
                    {enMarcha && <th className="sin-imprimir" />}
                  </tr>
                </thead>
                {grupos.map((grupo) => {
                  const pendientes = grupo.lineas.filter((l) => l.contadoMg === null && !l.saltado).length;
                  const hechas = grupo.lineas.filter((l) => l.contadoMg !== null && !l.saltado).length;
                  return (
                    <tbody key={grupo.id}>
                      <tr className="fila-grupo">
                        <td colSpan={enMarcha ? 5 : 4}>
                          <div className="fila separada">
                            <strong>
                              {grupo.nombre}{" "}
                              <span className="suave pequeno">
                                {hechas} de {grupo.lineas.length}
                              </span>
                            </strong>
                            {enMarcha && pendientes > 0 && (
                              <button
                                className="boton fantasma sin-imprimir"
                                onClick={() => void saltarResto(grupo.id)}
                              >
                                {t("re.d.saltarElResto")}
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                      {grupo.lineas.map((l) => {
                    const diferencia = l.contadoMg !== null && !l.saltado ? l.contadoMg - l.esperadoMg : null;
                    return (
                      <tr key={l.id} className={l.saltado ? "anulada" : undefined}>
                        <td>{l.producto.nombre}</td>
                        <td className="num tabular suave">{gramos(l.esperadoMg)}</td>
                        <td className="num">
                          {enMarcha ? (
                            <input
                              type="number"
                              step="0.01"
                              min={0}
                              className="celda-numero"
                              defaultValue={l.contadoMg !== null ? l.contadoMg / 1000 : ""}
                              placeholder="—"
                              onBlur={(e) => guardarPesada(l, e.currentTarget.value)}
                              onKeyDown={(e) => {
                                /*
                                 * Con la balanza delante se teclea el numero y
                                 * se pulsa Enter, sin soltar el bote para ir a
                                 * pinchar a otra casilla. Enter guarda y salta
                                 * al siguiente, que es como se cuenta de verdad.
                                 */
                                if (e.key !== "Enter") return;
                                e.preventDefault();
                                guardarPesada(l, e.currentTarget.value);
                                const casillas = [
                                  ...document.querySelectorAll<HTMLInputElement>("input.celda-numero"),
                                ];
                                casillas[casillas.indexOf(e.currentTarget) + 1]?.focus();
                              }}
                            />
                          ) : l.saltado ? (
                            <span className="suave pequeno">{t("re.d.saltado")}</span>
                          ) : l.contadoMg !== null ? (
                            <span className="tabular">{gramos(l.contadoMg)}</span>
                          ) : (
                            <span className="suave">—</span>
                          )}
                        </td>
                        <td className="num">
                          {diferencia !== null ? <Descuadre mg={diferencia} /> : <span className="suave">—</span>}
                        </td>
                        {enMarcha && (
                          <td className="sin-imprimir">
                            <button
                              className="boton fantasma"
                              title={l.saltado ? t("re.d.volverAContar") : t("re.d.saltarEste")}
                              onClick={() => void apuntar(l, { saltado: !l.saltado })}
                            >
                              {l.saltado ? t("re.d.contar") : t("re.d.saltar")}
                            </button>
                          </td>
                        )}
                      </tr>
                    );
                      })}
                    </tbody>
                  );
                })}
              </table>
            </div>

            {!enMarcha && detalle.data.notas && (
              <p className="suave pequeno">
                <strong>{t("re.d.notas")}</strong> {detalle.data.notas}
              </p>
            )}
          </div>

          {!enMarcha && (
            <p className="suave pequeno sin-imprimir" style={{ marginBottom: 0 }}>
              {detalle.data.ajustoStock ? t("re.d.seCorrigio") : t("re.d.noSeCorrigio")}
            </p>
          )}
        </>
      )}

      {cerrando && (
        <Modal
          titulo={t("re.c.titulo")}
          alCerrar={() => setCerrando(false)}
          acciones={
            <>
              <button className="boton secundario" onClick={() => setCerrando(false)}>
                {t("re.c.volver")}
              </button>
              <button
                className="boton"
                disabled={enviando}
                onClick={async () => {
                  setEnviando(true);
                  try {
                    await api(`/recuentos/${id}/cerrar`, {
                      metodo: "POST",
                      cuerpo: { ajustarStock: ajustar, notas: notas || undefined },
                    });
                    alCambiar();
                    setCerrando(false);
                    alCerrar();
                  } catch (fallo) {
                    alFallar(fallo instanceof Error ? fallo.message : t("re.c.fallo"));
                    setEnviando(false);
                  }
                }}
              >
                {enviando ? t("re.c.cerrando") : t("re.c.cerrar")}
              </button>
            </>
          }
        >
          <p style={{ marginTop: 0 }}>
            {cuenta(contadas.length, "re.c.hasContadoUno", "re.c.hasContado")} <Descuadre mg={descuadre} />.
          </p>

          <label className="casilla">
            <input type="checkbox" checked={ajustar} onChange={(e) => setAjustar(e.target.checked)} />
            <span>
              {t("re.c.corregir")}
              <br />
              <span className="ayuda">{t("re.c.corregirAyuda")}</span>
            </span>
          </label>

          <Campo etiqueta={t("re.c.notas")} ayuda={t("re.c.notasAyuda")}>
            <textarea rows={2} value={notas} onChange={(e) => setNotas(e.target.value)} maxLength={500} />
          </Campo>

          <p className="suave pequeno" style={{ marginBottom: 0 }}>
            {t("re.c.noSeBorra")}
          </p>
        </Modal>
      )}
    </Modal>
  );
}
